import { useState } from "react";
import { ClipboardCopy, Check, Landmark } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "./Reveal";

const STEPS = [
  {
    title: "Find your benefits contact",
    body: "For an FSA, contact your benefits administrator using the number on your benefits card or your employer\u2019s benefits portal. For an HSA, contact your account provider for payment and recordkeeping guidance. A tax professional can help with questions about whether an expense qualifies.",
  },
  {
    title: "Ask before you book",
    body: "Use the script below to ask about your visit, required documentation, and reimbursement.",
  },
  {
    title: "Keep your paperwork",
    body: "Ask the practice about an itemized receipt. Keep receipts and any supporting documents for your records.",
  },
];

const QUESTIONS = [
  "Does my account have restrictions that would exclude this type of visit?",
  "What documentation should I request from the practice?",
  "Is an itemized receipt sufficient, or is additional documentation needed?",
  "If I pay out of pocket, what is the reimbursement process?",
  "Are there service-date requirements or claim-submission deadlines?",
  "If labs or prescriptions are recommended, should I check their eligibility separately?",
];

const SCRIPT_INTRO =
  "Hi! I\u2019m considering a 60-minute, $400 appointment with Dr. Jill Crosson, DO, at All-In Precision Wellness to evaluate perimenopause or menopause symptoms. This is a self-pay appointment.";
const SCRIPT_ASK =
  "Can you help me understand whether this expense may qualify for payment or reimbursement through my account?";
const SCRIPT_TEXT = `${SCRIPT_INTRO}\n\n${SCRIPT_ASK}\n\n${QUESTIONS.map((q) => `\u2022 ${q}`).join("\n")}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  }
};

export const HsaFsaSection = ({ onContact }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyText(SCRIPT_TEXT);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Reveal>
      <div
        data-testid="hsa-fsa-section"
        id="hsa-fsa"
        className="mt-8 bg-white border border-[var(--brand-gold-soft)] p-8 sm:p-10"
      >
        <div className="flex items-start gap-4">
          <span
            className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center bg-[var(--brand-cream)] text-[var(--brand-gold)]"
            aria-hidden="true"
          >
            <Landmark className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-serif-brand text-2xl font-medium text-[var(--brand-navy)]">
              Your HSA or FSA may help pay for your visit.
            </h3>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-[var(--brand-muted)]">
              Have a Health Savings Account (HSA) or Flexible Spending Account (FSA)? You may be
              able to use those funds for eligible medical expenses, including a consultation to
              evaluate perimenopause or menopause symptoms. Eligibility depends on the services
              provided and your account&rsquo;s rules.
            </p>
          </div>
        </div>

        <ol data-testid="hsa-fsa-steps" className="mt-8 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={i} className="border-t-2 border-[var(--brand-gold)] pt-4">
              <p className="font-serif-brand text-3xl font-medium text-[var(--brand-gold)]" aria-hidden="true">
                {i + 1}
              </p>
              <p className="mt-1 font-semibold text-[var(--brand-navy)]">{s.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-[var(--brand-muted)]">{s.body}</p>
            </li>
          ))}
        </ol>

        <Accordion type="single" collapsible className="mt-8">
          <AccordionItem value="script" className="border border-[var(--brand-gold-soft)] bg-[var(--brand-cream)] px-5 sm:px-6">
            <AccordionTrigger
              data-testid="hsa-fsa-script-trigger"
              className="py-4 text-base font-semibold text-[var(--brand-navy)] hover:no-underline"
            >
              What should I say?
            </AccordionTrigger>
            <AccordionContent data-testid="hsa-fsa-script-panel" className="pb-6">
              <blockquote className="text-base leading-relaxed text-[var(--brand-navy-deep)]">
                <p>&ldquo;{SCRIPT_INTRO}</p>
                <p className="mt-3">{SCRIPT_ASK}</p>
                <ul className="mt-3 space-y-2">
                  {QUESTIONS.map((q, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="shrink-0 mt-2 h-1.5 w-1.5 bg-[var(--brand-gold)]" aria-hidden="true" />
                      <span>
                        {q}
                        {i === QUESTIONS.length - 1 && <>&rdquo;</>}
                      </span>
                    </li>
                  ))}
                </ul>
              </blockquote>
              <button
                type="button"
                data-testid="hsa-fsa-copy-button"
                onClick={handleCopy}
                className="mt-5 inline-flex items-center gap-2 bg-[var(--brand-navy)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--brand-navy-deep)]"
              >
                {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <ClipboardCopy className="h-4 w-4" aria-hidden="true" />}
                {copied ? "Copied!" : "Copy these questions"}
              </button>
              <span className="sr-only" role="status" aria-live="polite">
                {copied ? "Questions copied to clipboard" : ""}
              </span>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <p className="mt-5 text-xs leading-relaxed text-[var(--brand-muted)]">
          HSA/FSA eligibility is separate from insurance coverage and is not guaranteed. HSA
          account holders are responsible for confirming that withdrawals are used for qualified
          expenses. The same expense cannot be reimbursed twice.
        </p>

        <button
          type="button"
          data-testid="hsa-fsa-contact-link"
          onClick={() =>
            onContact?.(
              "Hi, I have a question about visit documentation for my HSA/FSA (for example, an itemized receipt)."
            )
          }
          className="mt-4 text-sm font-medium text-[var(--brand-navy)] underline decoration-[var(--brand-gold)] underline-offset-4 transition-colors hover:text-[var(--brand-gold)]"
        >
          Questions about visit documentation? Contact our office.
        </button>
      </div>
    </Reveal>
  );
};
